/**
 * Sysadmin MCP catalog — Wave 4 PR-F-rebuild.
 *
 * TanStack Query hooks for the sysadmin admin endpoints under
 * `/api/v1/admin/platform/mcp`. Every hook here is sysadmin-only;
 * the apiClient flows the platform-admin JWT (audience
 * `argusai-admin`) automatically.
 *
 * Wire types match the Zod response schemas in
 * `packages/api/src/routes/v1/admin/mcp.ts`. Per I-M6.7 these admin
 * types DO include internal connection_config + auth_scheme — those
 * are sysadmin-only views. The tenant-side `/api/v1/mcp-catalog/
 * available` endpoint has a strict redacted schema and is consumed
 * by `useTenantMcpCatalog` (separate hook in lib/use-tenant-mcp-
 * catalog.ts) — kept distinct so the two surfaces never share types.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';
import type { McpTenantConfigInput } from '@argusai/shared/schemas/mcp-install.schema';

// ─── Constants ────────────────────────────────────────────────────────

export const MCP_HOSTING_MODES = [
  'platform_hosted',
  'remote_hosted',
  'rest_wrapped',
] as const;
export type McpHostingMode = (typeof MCP_HOSTING_MODES)[number];

export const MCP_PROTOCOLS = [
  'stdio',
  'sse',
  'http',
  'rest',
  'graphql',
  'sql',
] as const;
export type McpProtocol = (typeof MCP_PROTOCOLS)[number];

export const MCP_TRUST_LEVELS = [
  'official',
  'verified',
  'community',
  'experimental',
] as const;
export type McpTrustLevel = (typeof MCP_TRUST_LEVELS)[number];

export const MCP_MARKETPLACE_CATEGORIES = [
  'data',
  'devops',
  'productivity',
  'ai-ml',
  'monitoring',
  'communication',
  'storage',
  'security',
] as const;
export type McpMarketplaceCategory = (typeof MCP_MARKETPLACE_CATEGORIES)[number];

// ─── Wire types ───────────────────────────────────────────────────────

export interface SysadminMcpCatalogRow {
  id: string;
  slug: string;
  displayName: string;
  description: string;
  icon: string;
  hostingMode: McpHostingMode;
  protocol: McpProtocol;
  defaultConfig: Record<string, unknown>;
  authScheme: Record<string, unknown>;
  manifest: unknown[];
  installInstructions: string | null;
  isPublished: boolean;
  version: number;
  retiredAt: string | null;
  categoryId: string | null;
  rating: number;
  reviewCount: number;
  installCount: number;
  author: string;
  license: string;
  marketplaceCategory: McpMarketplaceCategory;
  trustLevel: McpTrustLevel;
  dockerImage: string | null;
  requiresAuth: boolean;
  marketplaceTags: string[];
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  activeInstallCount: number;
  cursor: string;
}

export interface CatalogListResponse {
  items: SysadminMcpCatalogRow[];
  nextCursor: string | null;
}

export interface CatalogListQuery {
  search?: string;
  hostingMode?: McpHostingMode;
  trustLevel?: McpTrustLevel;
  marketplaceCategory?: McpMarketplaceCategory;
  includeRetired?: boolean;
  publishedOnly?: boolean;
  cursor?: string;
  limit?: number;
}

export interface InstallReportRow {
  id: string;
  organizationId: string;
  organizationName: string;
  installedVersion: number;
  isActive: boolean;
  healthStatus: string;
  installedAt: string;
  installedBy: string | null;
  lastHealthCheck: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  revocationReason: string | null;
}

export interface TenantAssignmentRow {
  id: string;
  systemServerId: string;
  slug: string;
  displayName: string;
  hostingMode: McpHostingMode;
  protocol: McpProtocol;
  trustLevel: McpTrustLevel;
  installedVersion: number;
  isActive: boolean;
  healthStatus: string;
  installedAt: string;
  revokedAt: string | null;
  revocationReason: string | null;
}

// ─── Mutation inputs ──────────────────────────────────────────────────

export interface CreateCatalogInput {
  slug: string;
  displayName: string;
  description: string;
  icon?: string;
  hostingMode: McpHostingMode;
  protocol: McpProtocol;
  defaultConfig?: Record<string, unknown>;
  authScheme?: Record<string, unknown>;
  manifest?: unknown[];
  installInstructions?: string;
  marketplaceCategory?: McpMarketplaceCategory;
  trustLevel?: McpTrustLevel;
  dockerImage?: string | null;
  requiresAuth?: boolean;
  marketplaceTags?: string[];
  author?: string;
  license?: string;
}

export interface UpdateCatalogInput {
  id: string;
  displayName?: string;
  description?: string;
  icon?: string;
  hostingMode?: McpHostingMode;
  protocol?: McpProtocol;
  defaultConfig?: Record<string, unknown>;
  authScheme?: Record<string, unknown>;
  manifest?: unknown[];
  installInstructions?: string | null;
  marketplaceCategory?: McpMarketplaceCategory;
  trustLevel?: McpTrustLevel;
  dockerImage?: string | null;
  requiresAuth?: boolean;
  marketplaceTags?: string[];
  author?: string;
  license?: string;
}

export interface AssignToTenantInput {
  organizationId: string;
  systemServerId: string;
  endpointOverride?: string;
  /** The one strict install-config shape (SECC-T1); the api seals any credential. */
  tenantConfig?: McpTenantConfigInput;
}

export interface RevokeAssignmentInput {
  id: string;
  reason?: string;
}

// ─── Query keys ───────────────────────────────────────────────────────

const KEYS = {
  catalog: (q: CatalogListQuery) => ['sysadmin', 'mcp', 'catalog', q] as const,
  detail: (id: string) => ['sysadmin', 'mcp', 'catalog', id] as const,
  installs: (id: string, includeRevoked: boolean) =>
    ['sysadmin', 'mcp', 'catalog', id, 'installs', includeRevoked] as const,
  tenantAssignments: (tenantId: string, includeRevoked: boolean) =>
    ['sysadmin', 'mcp', 'tenant-assignments', tenantId, includeRevoked] as const,
};

// ─── Query hooks ──────────────────────────────────────────────────────

export function useSysadminMcpCatalog(query: CatalogListQuery = {}) {
  const params = new URLSearchParams();
  if (query.search) params.set('search', query.search);
  if (query.hostingMode) params.set('hostingMode', query.hostingMode);
  if (query.trustLevel) params.set('trustLevel', query.trustLevel);
  if (query.marketplaceCategory) params.set('marketplaceCategory', query.marketplaceCategory);
  if (query.includeRetired) params.set('includeRetired', 'true');
  if (query.publishedOnly) params.set('publishedOnly', 'true');
  if (query.cursor) params.set('cursor', query.cursor);
  if (query.limit) params.set('limit', String(query.limit));
  const qs = params.toString();

  return useQuery<CatalogListResponse, Error>({
    queryKey: KEYS.catalog(query),
    queryFn: () =>
      apiClient.get<CatalogListResponse>(
        `/admin/platform/mcp/catalog${qs ? `?${qs}` : ''}`,
      ),
  });
}

export function useSysadminMcpCatalogRow(id: string | null | undefined) {
  return useQuery<SysadminMcpCatalogRow, Error>({
    queryKey: id ? KEYS.detail(id) : ['sysadmin', 'mcp', 'catalog', '__none__'],
    queryFn: () =>
      apiClient.get<SysadminMcpCatalogRow>(
        `/admin/platform/mcp/catalog/${id}`,
      ),
    enabled: !!id,
  });
}

export function useSysadminMcpInstalls(
  id: string | null | undefined,
  includeRevoked = false,
) {
  return useQuery<{ items: InstallReportRow[] }, Error>({
    queryKey: id
      ? KEYS.installs(id, includeRevoked)
      : ['sysadmin', 'mcp', 'installs', '__none__'],
    queryFn: () =>
      apiClient.get<{ items: InstallReportRow[] }>(
        `/admin/platform/mcp/catalog/${id}/installs${includeRevoked ? '?includeRevoked=true' : ''}`,
      ),
    enabled: !!id,
  });
}

export function useTenantMcpAssignments(
  tenantId: string | null | undefined,
  includeRevoked = false,
) {
  return useQuery<{ items: TenantAssignmentRow[] }, Error>({
    queryKey: tenantId
      ? KEYS.tenantAssignments(tenantId, includeRevoked)
      : ['sysadmin', 'mcp', 'tenant-assignments', '__none__'],
    queryFn: () =>
      apiClient.get<{ items: TenantAssignmentRow[] }>(
        `/admin/platform/mcp/tenants/${tenantId}/assignments${includeRevoked ? '?includeRevoked=true' : ''}`,
      ),
    enabled: !!tenantId,
  });
}

// ─── Mutation hooks ───────────────────────────────────────────────────

export function useCreateMcpCatalog() {
  const qc = useQueryClient();
  return useMutation<{ id: string }, Error, CreateCatalogInput>({
    mutationFn: (input) =>
      apiClient.post('/admin/platform/mcp/catalog', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sysadmin', 'mcp', 'catalog'] });
    },
  });
}

export function useUpdateMcpCatalog() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, UpdateCatalogInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient.patch(`/admin/platform/mcp/catalog/${id}`, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['sysadmin', 'mcp', 'catalog'] });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
}

export function useDeleteMcpCatalog() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.delete(`/admin/platform/mcp/catalog/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sysadmin', 'mcp', 'catalog'] });
    },
  });
}

export function useMcpCatalogLifecycle() {
  const qc = useQueryClient();

  const invalidate = (id?: string) => {
    qc.invalidateQueries({ queryKey: ['sysadmin', 'mcp', 'catalog'] });
    if (id) qc.invalidateQueries({ queryKey: KEYS.detail(id) });
  };

  const publish = useMutation<{ ok: true; version: number }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.post(`/admin/platform/mcp/catalog/${id}/publish`, {}),
    onSuccess: (_d, v) => invalidate(v.id),
  });
  const unpublish = useMutation<{ ok: true }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.post(`/admin/platform/mcp/catalog/${id}/unpublish`, {}),
    onSuccess: (_d, v) => invalidate(v.id),
  });
  const retire = useMutation<{ ok: true }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.post(`/admin/platform/mcp/catalog/${id}/retire`, {}),
    onSuccess: (_d, v) => invalidate(v.id),
  });
  const versionBump = useMutation<{ version: number }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.post(`/admin/platform/mcp/catalog/${id}/version-bump`, {}),
    onSuccess: (_d, v) => invalidate(v.id),
  });

  return { publish, unpublish, retire, versionBump };
}

export function useAssignMcpToTenant() {
  const qc = useQueryClient();
  return useMutation<
    { id: string; alreadyExists?: true },
    Error,
    AssignToTenantInput
  >({
    mutationFn: (input) =>
      apiClient.post('/admin/platform/mcp/tenant-assignments', input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['sysadmin', 'mcp'] });
      qc.invalidateQueries({
        queryKey: KEYS.tenantAssignments(vars.organizationId, false),
      });
      qc.invalidateQueries({
        queryKey: KEYS.tenantAssignments(vars.organizationId, true),
      });
    },
  });
}

export function useRevokeMcpAssignment() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, RevokeAssignmentInput>({
    mutationFn: ({ id, reason }) =>
      apiClient.delete(`/admin/platform/mcp/tenant-assignments/${id}`, {
        body: JSON.stringify({ reason }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sysadmin', 'mcp'] });
    },
  });
}
